import { Box, Flex, List, Stack, Text, VisuallyHidden } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Drawer } from '@time-line/components/common';
import { PriceVATMessage } from 'components/common/PriceVATMessage';
import { FormatPrice } from 'components/localisation/FormatPrice';
import { useCart } from 'hooks/cart/useCart';
import { useCartCount } from 'hooks/cart/useCartCount';
import { useCartUI } from 'hooks/cart/useCartUI';
import { useUpdateCartBuyerIdentity } from 'hooks/cart/useUpdateCartBuyerIdentity';
import { useAnalytics } from 'hooks/useAnalytics';
import { useCheckout } from 'hooks/useCheckout';
import { useLocalStorage, UserLocale } from 'hooks/userPreferences';
import { env } from 'lib/constants/env';
import { SHOP } from 'lib/constants/routes';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { CurrencyCode } from 'services/shopify';
import { getSavingsRate } from 'utils/getSavingsRate';
import { isFreeShippingCountry } from 'utils/localisation/isFreeShippingCountry';

import { CartDiscount } from './CartDiscount';
import { CartFreeShippingProgress } from './CartFreeShippingProgress';
import { CartLineItem } from './CartLineItem';
import { CartPromos } from './CartPromos';
import { CartTrustPoints } from './CartTrustPoints';

type CartProps = {
  isDiscountInputVisible?: boolean;
  isDiscountPercentageVisible?: boolean;
  isTrustPointsVisible?: boolean;
  isInfoBulletsVisible?: boolean;
  isFreeShippingProgressVisible?: boolean;
};

export const Cart = ({
  isDiscountInputVisible = true,
  isDiscountPercentageVisible = false,
  isTrustPointsVisible = false,
  isInfoBulletsVisible = false,
  isFreeShippingProgressVisible = false,
}: CartProps) => {
  const { analytics } = useAnalytics();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isCheckoutLoading, setIsCheckoutLoading, createCheckoutURL } =
    useCheckout();

  const { mutate: updateCartBuyerIdentity } =
    useUpdateCartBuyerIdentity(queryClient);
  const { data: cart } = useCart();
  const {
    value: { countryCode },
    isLoading: isLocaleLoading,
  } = useLocalStorage(UserLocale);

  useEffect(() => {
    if (!isLocaleLoading) {
      updateCartBuyerIdentity(countryCode);
    }
  }, [countryCode, updateCartBuyerIdentity, isLocaleLoading]);

  // UI
  const cartCount = useCartCount();
  const { isCartOpen, toggleCart } = useCartUI();
  const cartCountLabel = `${cartCount} item${cartCount > 1 ? 's' : ''}`;
  const viewCartFiredRef = useRef(false);
  const hasAppliedDiscountCodes = !!cart?.discountCodes?.some(
    (dC) => dC.applicable,
  );

  const handleContinueClick = () => {
    toggleCart(false);
    router.push(SHOP);
  };

  const discountPercentage =
    isDiscountPercentageVisible && cart
      ? getSavingsRate({
          anchor: cart.subTotal.amount,
          price: cart.totalPrice.amount,
        })
      : 0;

  const freeShippingThreshold = env.NEXT_PUBLIC_FREE_SHIPPING_THRESHOLD_USD;
  const isFreeShippingProgressEnabled =
    isFreeShippingProgressVisible && !!freeShippingThreshold;
  const isFreeShippingProgressShown =
    isFreeShippingProgressEnabled &&
    cartCount > 0 &&
    isFreeShippingCountry(countryCode) &&
    cart?.totalPrice.currencyCode === CurrencyCode.Usd;

  useEffect(() => {
    if (!isCartOpen) {
      viewCartFiredRef.current = false;
      return;
    }

    if (cart && cart.lines.length && !viewCartFiredRef.current) {
      viewCartFiredRef.current = true;
      analytics.viewCart({ cart });
    }
  }, [isCartOpen, cart, analytics]);

  useEffect(() => {
    setIsCheckoutLoading(false);
    toggleCart(false);
  }, [router.asPath, toggleCart, setIsCheckoutLoading]);

  return (
    <Drawer
      isOpen={isCartOpen}
      ariaLabel="cart"
      onClose={() => toggleCart(false)}
      bodyPt={0}
      bodyPb={0}
      width={{ base: '100%', md: '72.5%', xl: '50%' }}
      maxWidth="md"
    >
      <Flex data-testid="cart" flexDirection="column" height="100%">
        <Box>
          <Flex alignItems="center" justifyContent="space-between">
            <Text size="title" as="h2">
              Your Cart
            </Text>
            {!!cartCount && (
              <Text size="body2" color="blackAlpha.700">
                {cartCountLabel}
              </Text>
            )}
          </Flex>
          {!isFreeShippingProgressEnabled && <Divider mt="sm" />}
        </Box>
        {!cart || cartCount === 0 ? (
          <Flex
            height="100%"
            justifyContent="space-between"
            flexDir="column"
            pb="sm"
          >
            <Text color="blackAlpha.500" mt="lg" size="h3" as="h2">
              Your cart is empty
            </Text>
            <Button onClick={handleContinueClick}>Continue shopping</Button>
          </Flex>
        ) : (
          <Box flexGrow={1} mt="2xs">
            <Flex flexDir="column" height="100%" justifyContent="space-between">
              <Flex flexDir="column" flexGrow={1}>
                {isFreeShippingProgressShown && (
                  <CartFreeShippingProgress
                    subtotal={cart.totalPrice.amount}
                    threshold={freeShippingThreshold}
                  />
                )}
                <List as="ul" flexGrow={1} spacing={12} mb="lg">
                  {cart.lines.map((line, i) => (
                    <CartLineItem
                      key={line.id}
                      lineItem={line}
                      showDivider={i > 0}
                      isInfoBulletsVisible={isInfoBulletsVisible}
                    />
                  ))}
                  {env.NEXT_PUBLIC_GWP_PROMO_ENABLED && <CartPromos />}
                </List>

                <Box mt="auto">
                  {(isDiscountInputVisible ||
                    cart.discountAllocations.length > 0) && <Divider />}
                  {cart.discountAllocations.length > 0 && (
                    <Stack pt="2xs" spacing="4xs">
                      {cart.discountAllocations.map(
                        ({ code, discountedAmount }) => (
                          <Flex key={code} justifyContent="space-between">
                            <Text as="span" size="body2">
                              Discount ({`${code.toUpperCase()}`})
                            </Text>

                            {discountedAmount.amount > 0 && (
                              <FormatPrice
                                size="body2"
                                beforeText="-"
                                amount={discountedAmount.amount}
                              />
                            )}
                          </Flex>
                        ),
                      )}
                    </Stack>
                  )}
                </Box>
              </Flex>

              {(isDiscountInputVisible || hasAppliedDiscountCodes) && (
                <Box mt="2xs" mb="lg">
                  <CartDiscount isInputVisible={isDiscountInputVisible} />
                </Box>
              )}

              <Divider />
              <Box
                pt="3xs"
                pb={isTrustPointsVisible ? '3xs' : 'm'}
                position="sticky"
                zIndex={2}
                bottom={0}
                bgColor="gray.50"
              >
                <Flex
                  flexWrap="wrap"
                  alignItems="baseline"
                  justifyContent="space-between"
                  gap="4xs"
                >
                  <Text size="body2">
                    Subtotal{' '}
                    <PriceVATMessage
                      as="span"
                      size="body2"
                      color="blackAlpha.700"
                    />
                  </Text>
                  <Flex flexWrap="wrap" alignItems="baseline" gap="4xs">
                    {discountPercentage > 0 && (
                      <>
                        <Text
                          as="span"
                          size="title2"
                          color="red.300"
                          fontWeight="medium"
                          whiteSpace="nowrap"
                        >
                          Save {discountPercentage}%
                        </Text>
                        <VisuallyHidden>Was </VisuallyHidden>
                        <FormatPrice
                          size="title2"
                          color="gray.600"
                          opacity={0.36}
                          amount={cart.subTotal.amount}
                          isCurrencyCodeHidden
                          hasStrikeThrough
                        />
                        <VisuallyHidden>Now </VisuallyHidden>
                      </>
                    )}
                    <FormatPrice
                      as="p"
                      size="h2"
                      amount={cart.totalPrice.amount}
                    />
                  </Flex>
                </Flex>
                <Button
                  isLoading={isCheckoutLoading}
                  onClick={createCheckoutURL}
                  width="100%"
                  mt="3xs"
                >
                  Checkout
                </Button>
                {isTrustPointsVisible && <CartTrustPoints mt="3xs" />}
              </Box>
            </Flex>
          </Box>
        )}
      </Flex>
    </Drawer>
  );
};
